import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/PageHeader';
import { Card, SectionTitle } from '@/components/Card';
import { Badge, type BadgeTone } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/LoadingScreen';
import { useBusinessUnits } from '@/app/hooks';
import { useIsWideScreen } from '@/app/useIsWideScreen';
import { useOperationalAccess } from '@/app/useOperationalAccess';
import { ApiRequestError } from '@/services/api';
import { apService, suppliersService } from '@/services/operational.service';
import {
  PAYABLE_STATUS_TITLES,
  type PayableStatus,
  type Supplier,
} from '@/types/operational';
import { OperationalKpi } from '@/features/operational/OperationalKpi';
import { formatAmount, formatByCurrency, formatDate, hasAmount } from '@/features/operational/money';
import { v } from '@/theme/tokens';

const STATUS_TONE: Record<PayableStatus, BadgeTone> = {
  UNPAID: 'neutral',
  PARTIALLY_PAID: 'accent',
  PAID: 'positive',
  OVERDUE: 'negative',
};

const inputStyle = {
  padding: '7px 10px',
  border: `1px solid ${v('line')}`,
  borderRadius: 9,
  background: v('surface'),
  color: v('ink'),
  fontSize: 12.5,
  maxWidth: '100%',
  minWidth: 0,
} as const;

/**
 * Поставщики и текущий долг.
 *
 * Every figure on this page is derived from the ledger by the server: there
 * is no field anywhere here that sets an outstanding balance, because there
 * is no such column to set.
 */
export function SuppliersPage() {
  const access = useOperationalAccess();
  const wide = useIsWideScreen();
  const client = useQueryClient();
  const units = useBusinessUnits();
  const [params, setParams] = useSearchParams();

  const selectedId = params.get('supplier');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [invoiceFor, setInvoiceFor] = useState<Supplier | null>(null);
  const [paymentFor, setPaymentFor] = useState<Supplier | null>(null);

  const summary = useQuery({
    queryKey: ['operational', 'ap', 'summary'],
    queryFn: ({ signal }) => apService.summary(undefined, signal),
    enabled: access.can('SUPPLIERS_AP'),
  });

  const balances = useQuery({
    queryKey: ['operational', 'ap', 'balances'],
    queryFn: ({ signal }) => apService.balances(signal),
    enabled: access.can('SUPPLIERS_AP'),
  });

  const suppliers = useQuery({
    queryKey: ['operational', 'suppliers', search],
    queryFn: ({ signal }) => suppliersService.list(search, signal),
    enabled: access.can('SUPPLIERS_AP'),
  });

  const detail = useQuery({
    queryKey: ['operational', 'suppliers', 'detail', selectedId],
    queryFn: ({ signal }) => suppliersService.detail(selectedId as string, 6, signal),
    enabled: Boolean(selectedId),
  });

  const refresh = () => void client.invalidateQueries({ queryKey: ['operational'] });
  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiRequestError ? e.message : fallback);

  const createSupplier = useMutation({
    mutationFn: suppliersService.create,
    onSuccess: () => {
      setAddingSupplier(false);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось создать поставщика'),
  });

  const createPayable = useMutation({
    mutationFn: apService.createPayable,
    onSuccess: () => {
      setInvoiceFor(null);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось создать счёт'),
  });

  const createPayment = useMutation({
    mutationFn: apService.createPayment,
    onSuccess: () => {
      setPaymentFor(null);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось записать оплату'),
  });

  if (access.loading) return <Spinner />;
  if (!access.can('SUPPLIERS_AP')) {
    return (
      <>
        <PageHeader title="Поставщики и долги" />
        <EmptyState
          icon="lock"
          title="Модуль недоступен"
          description="Доступ к поставщикам и задолженности выдаёт владелец."
        />
      </>
    );
  }

  const canManage = access.can('SUPPLIERS_AP', 'MANAGE');
  const selected = detail.data;

  return (
    <>
      <PageHeader
        title="Поставщики и долги"
        subtitle="Текущая задолженность рассчитывается по счетам и оплатам"
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setAddingSupplier(true)}>
              + Поставщик
            </Button>
          ) : undefined
        }
      />

      {error && (
        <div
          role="alert"
          style={{
            background: v('negative-soft'),
            color: v('negative'),
            padding: '9px 12px',
            borderRadius: 10,
            fontSize: 13,
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      {summary.data && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(210px, 100%), 1fr))',
            gap: 14,
            marginBottom: 22,
          }}
        >
          <OperationalKpi
            label="Текущий долг"
            values={formatByCurrency(summary.data.outstanding_by_currency)}
            caption={`поставщиков с долгом: ${summary.data.suppliers_with_debt}`}
          />
          <OperationalKpi
            label="Просрочено"
            values={formatByCurrency(summary.data.overdue_by_currency)}
            caption={`счетов: ${summary.data.overdue_invoice_count}`}
            tone={hasAmount(summary.data.overdue_by_currency) ? 'negative' : 'neutral'}
          />
          <OperationalKpi
            label={`К оплате ${summary.data.due_soon_days} дней`}
            values={formatByCurrency(summary.data.due_soon_by_currency)}
            caption="по сроку оплаты"
            tone={hasAmount(summary.data.due_soon_by_currency) ? 'warning' : 'neutral'}
          />
        </div>
      )}

      <Card style={{ marginBottom: 18 }}>
        <SectionTitle right={<Badge tone="neutral">{suppliers.data?.length ?? 0}</Badge>}>
          Поставщики
        </SectionTitle>

        <input
          aria-label="Поиск поставщика"
          placeholder="Поиск по названию"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ ...inputStyle, width: '100%', marginBottom: 12 }}
        />

        {suppliers.isPending && <Spinner />}
        {suppliers.data?.length === 0 && (
          <div style={{ fontSize: 13, color: v('ink-muted') }}>Поставщиков пока нет.</div>
        )}

        {(suppliers.data ?? []).map((item) => {
          const balance = (balances.data ?? []).find((b) => b.supplier_id === item.id);
          return (
            <div
              key={item.id}
              style={{
                display: 'flex',
                gap: 12,
                padding: '11px 0',
                borderTop: `1px solid ${v('line-soft')}`,
                alignItems: wide ? 'center' : 'flex-start',
                flexDirection: wide ? 'row' : 'column',
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setParams((current) => {
                    current.set('supplier', item.id);
                    return current;
                  })
                }
                style={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: v('ink'),
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{item.name}</div>
                <div style={{ fontSize: 12, color: v('ink-muted') }}>
                  {balance
                    ? `${formatByCurrency(balance.outstanding_by_currency).join(' · ')} · счетов: ${balance.open_invoice_count}`
                    : 'Задолженности нет'}
                </div>
              </button>

              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {balance && hasAmount(balance.overdue_by_currency) && (
                  <Badge tone="negative">
                    Просрочено {formatByCurrency(balance.overdue_by_currency).join(' · ')}
                  </Badge>
                )}
                {canManage && (
                  <>
                    <Button
                      style={{ padding: '6px 10px', fontSize: 12 }}
                      onClick={() => setInvoiceFor(item)}
                    >
                      + Счёт
                    </Button>
                    <Button
                      style={{ padding: '6px 10px', fontSize: 12 }}
                      onClick={() => setPaymentFor(item)}
                    >
                      + Оплата
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </Card>

      {selectedId && detail.isPending && <Spinner />}

      {selected && (
        <>
          <Card style={{ marginBottom: 18 }}>
            <SectionTitle
              right={
                <Button
                  style={{ padding: '6px 12px', fontSize: 12.5 }}
                  onClick={() =>
                    setParams((current) => {
                      current.delete('supplier');
                      return current;
                    })
                  }
                >
                  Закрыть
                </Button>
              }
            >
              {selected.supplier.name}
            </SectionTitle>

            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14 }}>
              <Metric
                label="Текущий долг"
                value={formatByCurrency(selected.balance.outstanding_by_currency).join(' · ')}
              />
              <Metric
                label="Просрочено"
                value={formatByCurrency(selected.balance.overdue_by_currency).join(' · ')}
                tone={hasAmount(selected.balance.overdue_by_currency) ? 'negative' : undefined}
              />
              <Metric
                label="Ближайший срок"
                value={formatDate(selected.balance.earliest_due_date)}
              />
              <Metric label="Открытых счетов" value={String(selected.balance.open_invoice_count)} />
            </div>

            {selected.history.length > 0 && (
              <>
                <div style={{ fontSize: 11.5, color: v('ink-muted'), marginBottom: 6 }}>
                  Динамика задолженности (на конец месяца)
                </div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {selected.history.map((point) => (
                    <div key={point.as_of} style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: v('ink-faint') }}>
                        {formatDate(point.as_of)}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {formatByCurrency(point.outstanding_by_currency).join(' · ')}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>

          <Card style={{ marginBottom: 18 }}>
            <SectionTitle>Обязательства</SectionTitle>
            {selected.invoices.length === 0 ? (
              <div style={{ fontSize: 13, color: v('ink-muted') }}>Счетов нет.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: v('ink-muted'), fontSize: 11.5 }}>
                      <th style={{ padding: '6px 8px 6px 0' }}>Документ</th>
                      <th style={{ padding: '6px 8px' }}>Дата</th>
                      <th style={{ padding: '6px 8px' }}>Срок</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Сумма</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Оплачено</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Остаток</th>
                      <th style={{ padding: '6px 0 6px 8px' }}>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.invoices.map((invoice) => (
                      <tr key={invoice.id} style={{ borderTop: `1px solid ${v('line-soft')}` }}>
                        <td style={{ padding: '7px 8px 7px 0' }}>
                          {invoice.document_number ?? '—'}
                          <div style={{ fontSize: 11, color: v('ink-faint') }}>
                            {invoice.business_unit_name}
                          </div>
                        </td>
                        <td style={{ padding: '7px 8px', whiteSpace: 'nowrap' }}>
                          {formatDate(invoice.document_date)}
                        </td>
                        <td style={{ padding: '7px 8px', whiteSpace: 'nowrap' }}>
                          {formatDate(invoice.due_date)}
                        </td>
                        <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {formatAmount(invoice.amount, invoice.currency)}
                        </td>
                        <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {formatAmount(invoice.paid_amount, invoice.currency)}
                        </td>
                        <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>
                          {formatAmount(invoice.outstanding_amount, invoice.currency)}
                        </td>
                        <td style={{ padding: '7px 0 7px 8px' }}>
                          <Badge tone={STATUS_TONE[invoice.status]}>
                            {PAYABLE_STATUS_TITLES[invoice.status]}
                          </Badge>
                          {invoice.days_overdue != null && (
                            <div style={{ fontSize: 11, color: v('negative'), marginTop: 2 }}>
                              {invoice.days_overdue} дн.
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <SectionTitle>Оплаты</SectionTitle>
            {selected.payments.length === 0 ? (
              <div style={{ fontSize: 13, color: v('ink-muted') }}>Оплат нет.</div>
            ) : (
              selected.payments.map((payment) => (
                <div
                  key={payment.id}
                  style={{
                    padding: '10px 0',
                    borderTop: `1px solid ${v('line-soft')}`,
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                    alignItems: 'baseline',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: 'nowrap' }}>
                    {formatAmount(payment.amount, payment.currency)}
                  </div>
                  <div style={{ fontSize: 12, color: v('ink-muted'), flex: 1, minWidth: 0 }}>
                    {formatDate(payment.payment_date)} · {payment.business_unit_name}
                    {payment.reference ? ` · ${payment.reference}` : ''}
                    {payment.allocations.length > 0
                      ? ` · разнесено по ${payment.allocations.length} счёт(ам)`
                      : ''}
                  </div>
                  {Number(payment.unallocated_amount) > 0 && (
                    <Badge tone="warning">
                      Не разнесено {formatAmount(payment.unallocated_amount, payment.currency)}
                    </Badge>
                  )}
                </div>
              ))
            )}
          </Card>
        </>
      )}

      {addingSupplier && (
        <SupplierDialog
          busy={createSupplier.isPending}
          onCancel={() => setAddingSupplier(false)}
          onSubmit={(input) => createSupplier.mutate(input)}
        />
      )}

      {invoiceFor && (
        <PayableDialog
          supplier={invoiceFor}
          units={units.data ?? []}
          busy={createPayable.isPending}
          onCancel={() => setInvoiceFor(null)}
          onSubmit={(input) => createPayable.mutate(input)}
        />
      )}

      {paymentFor && (
        <PaymentDialog
          supplier={paymentFor}
          units={units.data ?? []}
          busy={createPayment.isPending}
          onCancel={() => setPaymentFor(null)}
          onSubmit={(input) => createPayment.mutate(input)}
        />
      )}
    </>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'negative' | 'warning';
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11.5, color: v('ink-muted') }}>{label}</div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: tone === 'negative' ? v('negative') : tone === 'warning' ? v('warning') : v('ink'),
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Modal({
  title,
  children,
  onCancel,
}: {
  title: string;
  children: React.ReactNode;
  onCancel: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="rt-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 60,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="rt-dialog"
        style={{
          borderRadius: 16,
          padding: 20,
          width: 440,
          maxWidth: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <span style={{ fontSize: 11.5, color: v('ink-muted'), display: 'block', marginBottom: 4 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function SupplierDialog({
  busy,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: { name: string; tax_id?: string; phone?: string }) => void;
}) {
  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [phone, setPhone] = useState('');

  return (
    <Modal title="Новый поставщик" onCancel={onCancel}>
      <Field label="Название">
        <input
          aria-label="Название поставщика"
          value={name}
          onChange={(event) => setName(event.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        />
      </Field>
      <Field label="ИНН / налоговый номер">
        <input
          aria-label="Налоговый номер"
          value={taxId}
          onChange={(event) => setTaxId(event.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        />
      </Field>
      <Field label="Телефон">
        <input
          aria-label="Телефон"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        />
      </Field>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <Button
          variant="primary"
          disabled={busy || !name.trim()}
          onClick={() =>
            onSubmit({
              name: name.trim(),
              tax_id: taxId.trim() || undefined,
              phone: phone.trim() || undefined,
            })
          }
        >
          {busy ? 'Сохранение…' : 'Создать'}
        </Button>
        <Button onClick={onCancel}>Отмена</Button>
      </div>
    </Modal>
  );
}

function PayableDialog({
  supplier,
  units,
  busy,
  onCancel,
  onSubmit,
}: {
  supplier: Supplier;
  units: { id: string; name: string }[];
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: {
    supplier_id: string;
    business_unit_id: string;
    document_date: string;
    amount: string;
    currency: string;
    document_number?: string;
    due_date?: string;
  }) => void;
}) {
  const [unitId, setUnitId] = useState(units[0]?.id ?? '');
  const [number, setNumber] = useState('');
  const [documentDate, setDocumentDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('AMD');

  return (
    <Modal title={`Счёт — ${supplier.name}`} onCancel={onCancel}>
      <Field label="Объект">
        <select
          aria-label="Объект"
          value={unitId}
          onChange={(event) => setUnitId(event.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        >
          {units.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Номер документа">
        <input
          aria-label="Номер документа"
          value={number}
          onChange={(event) => setNumber(event.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        />
      </Field>
      <Field label="Дата документа">
        <input
          type="date"
          aria-label="Дата документа"
          value={documentDate}
          onChange={(event) => setDocumentDate(event.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        />
      </Field>
      <Field label="Срок оплаты">
        <input
          type="date"
          aria-label="Срок оплаты"
          value={dueDate}
          onChange={(event) => setDueDate(event.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        />
      </Field>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Сумма">
            <input
              aria-label="Сумма счёта"
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            />
          </Field>
        </div>
        <div style={{ width: 100 }}>
          <Field label="Валюта">
            <select
              aria-label="Валюта счёта"
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            >
              {['AMD', 'RUB', 'USD', 'EUR'].map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <Button
          variant="primary"
          disabled={busy || !amount.trim() || !unitId}
          onClick={() =>
            onSubmit({
              supplier_id: supplier.id,
              business_unit_id: unitId,
              document_date: documentDate,
              amount: amount.trim().replace(',', '.'),
              currency,
              document_number: number.trim() || undefined,
              due_date: dueDate || undefined,
            })
          }
        >
          {busy ? 'Сохранение…' : 'Создать счёт'}
        </Button>
        <Button onClick={onCancel}>Отмена</Button>
      </div>
    </Modal>
  );
}

function PaymentDialog({
  supplier,
  units,
  busy,
  onCancel,
  onSubmit,
}: {
  supplier: Supplier;
  units: { id: string; name: string }[];
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: {
    supplier_id: string;
    business_unit_id: string;
    payment_date: string;
    amount: string;
    currency: string;
    reference?: string;
    allocations?: { invoice_id: string; amount: string }[];
    auto_allocate?: boolean;
  }) => void;
}) {
  const [unitId, setUnitId] = useState(units[0]?.id ?? '');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('AMD');
  const [reference, setReference] = useState('');
  const [autoAllocate, setAutoAllocate] = useState(false);
  const [allocations, setAllocations] = useState<Record<string, string>>({});

  const open = useQuery({
    queryKey: ['operational', 'ap', 'payables', supplier.id, unitId],
    queryFn: ({ signal }) =>
      apService.payables({ supplier_id: supplier.id, business_unit_id: unitId, only_open: true }, signal),
    enabled: Boolean(unitId),
  });

  const chosen = Object.entries(allocations)
    .filter(([, value]) => value.trim())
    .map(([invoice_id, value]) => ({ invoice_id, amount: value.trim().replace(',', '.') }));

  return (
    <Modal title={`Оплата — ${supplier.name}`} onCancel={onCancel}>
      <Field label="Объект">
        <select
          aria-label="Объект оплаты"
          value={unitId}
          onChange={(event) => setUnitId(event.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        >
          {units.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Дата оплаты">
        <input
          type="date"
          aria-label="Дата оплаты"
          value={paymentDate}
          onChange={(event) => setPaymentDate(event.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        />
      </Field>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Сумма">
            <input
              aria-label="Сумма оплаты"
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            />
          </Field>
        </div>
        <div style={{ width: 100 }}>
          <Field label="Валюта">
            <select
              aria-label="Валюта оплаты"
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            >
              {['AMD', 'RUB', 'USD', 'EUR'].map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>
      <Field label="Назначение / номер">
        <input
          aria-label="Назначение оплаты"
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        />
      </Field>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11.5, color: v('ink-muted'), marginBottom: 6 }}>
          Разнести по счетам
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 8 }}>
          <input
            type="checkbox"
            aria-label="Разнести автоматически, начиная с самых старых"
            checked={autoAllocate}
            onChange={(event) => setAutoAllocate(event.target.checked)}
          />
          Разнести автоматически, начиная с самых старых
        </label>

        {!autoAllocate &&
          (open.data ?? [])
            .filter((invoice) => invoice.currency === currency)
            .map((invoice) => (
              <div
                key={invoice.id}
                style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}
              >
                <div style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                  {invoice.document_number ?? formatDate(invoice.document_date)}
                  <span style={{ color: v('ink-muted') }}>
                    {' '}
                    · остаток {formatAmount(invoice.outstanding_amount, invoice.currency)}
                  </span>
                </div>
                <input
                  aria-label={`Сумма к счёту ${invoice.document_number ?? invoice.id}`}
                  inputMode="decimal"
                  placeholder="0"
                  value={allocations[invoice.id] ?? ''}
                  onChange={(event) =>
                    setAllocations((current) => ({ ...current, [invoice.id]: event.target.value }))
                  }
                  style={{ ...inputStyle, width: 110 }}
                />
              </div>
            ))}
        {!autoAllocate && (open.data ?? []).length === 0 && (
          <div style={{ fontSize: 12, color: v('ink-muted') }}>
            Открытых счетов нет — оплата останется неразнесённой и будет видна как таковая.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          variant="primary"
          disabled={busy || !amount.trim() || !unitId}
          onClick={() =>
            onSubmit({
              supplier_id: supplier.id,
              business_unit_id: unitId,
              payment_date: paymentDate,
              amount: amount.trim().replace(',', '.'),
              currency,
              reference: reference.trim() || undefined,
              allocations: autoAllocate ? [] : chosen,
              auto_allocate: autoAllocate,
            })
          }
        >
          {busy ? 'Сохранение…' : 'Записать оплату'}
        </Button>
        <Button onClick={onCancel}>Отмена</Button>
      </div>
    </Modal>
  );
}
